import { useRef } from 'react'

import { useDebounceFunc } from '../../../../../hooks'
import {
  GRETA_AI_CHAT_SETTLED_GENERATION_GRACE_MS,
  GRETA_AI_CHAT_STATUS_POLL_MS,
} from '../../../GretaAIChat.constants'
import {
  getGretaAIChatDeliveredTurn,
  getHasGretaAIChatDeliveredTurn,
} from '../useGretaAIChatHistory.utils'

// Right after a reply the page can lag behind it (read replica): until a page holds the
// delivered turn, loads retry through `onSync` and a silent one leaves the feed as is.
export function useGretaAIChatDeliveredTurns({ onSync }) {
  const deliveredTurnsRef = useRef(new Map())

  const {
    cancelDebounce: cancelDeliveredTurnSync,
    debouncedHandler: debounceDeliveredTurnSync,
  } = useDebounceFunc(onSync, GRETA_AI_CHAT_STATUS_POLL_MS)

  function trackDeliveredTurn(id, payloadMessages) {
    deliveredTurnsRef.current.set(id, {
      ...getGretaAIChatDeliveredTurn(payloadMessages),
      expiresAt: performance.now() + GRETA_AI_CHAT_SETTLED_GENERATION_GRACE_MS,
    })
  }

  function getDeliveredTurn(id) {
    return deliveredTurnsRef.current.get(id)
  }

  // A newer turn of the chat owns the feed's status from here.
  function forgetDeliveredTurn(id) {
    cancelDeliveredTurnSync()
    deliveredTurnsRef.current.delete(id)
  }

  function scheduleDeliveredTurnSync(id) {
    const turn = deliveredTurnsRef.current.get(id)

    if (turn && performance.now() < turn.expiresAt) {
      debounceDeliveredTurnSync(id)
    } else {
      deliveredTurnsRef.current.delete(id)
    }
  }

  function getIsDeliveredTurnLagging(id, loaded) {
    const turn = deliveredTurnsRef.current.get(id)

    if (turn && !getHasGretaAIChatDeliveredTurn(loaded, turn)) {
      scheduleDeliveredTurnSync(id)

      return true
    }

    forgetDeliveredTurn(id)

    return false
  }

  return {
    cancelDeliveredTurnSync,
    forgetDeliveredTurn,
    getDeliveredTurn,
    getIsDeliveredTurnLagging,
    scheduleDeliveredTurnSync,
    trackDeliveredTurn,
  }
}
